import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTheme } from 'styled-components';

import {
    QUERIES_ADMIN,
    ROUTES_ACCOUNT,
    ROUTES_ADMIN,
} from '@constants';

import { useRealState } from 'contexts/RealStateContext';

import { Button, HelperBoolean, HelperMoney, HelperOptions } from 'components/atoms';
import { Table } from 'components/organisms';
import { onErrorMessage, onSuccessMessage } from 'helpers';
import {
    getAdministratorPlanIndex,
    removeAdministratorPlan,
} from 'services';
import { sweetAlert } from 'utils';

import { PlanListWrap } from './styles';
import Layout from '../../Layout';

const PlanList = () => {
    const titleMessages = 'Planos!';
    const theme = useTheme();
    const navigate = useNavigate();
    const { getLinkWithSlug } = useRealState();

    const queryClient = useQueryClient();

    const { data, isLoading: isLoadingList } = useQuery(
        QUERIES_ADMIN.PLAN.base,
        () => getAdministratorPlanIndex()
    );

    const { mutate: removeRegister } =
        useMutation((id: number) => removeAdministratorPlan(String(id)), {
            mutationKey: QUERIES_ADMIN.PLAN.base,
            onSuccess: () => {
                onSuccessMessage(
                    titleMessages,
                    'Registro removido com sucesso!'
                );

                queryClient.invalidateQueries(QUERIES_ADMIN.PLAN.base);
            },
            onError: (err) => {
                onErrorMessage(titleMessages, err);
            },
        });

    function onHandleNavigate(param: string) {
        navigate(param);
    }

    function newRegister() {
        onHandleNavigate(getLinkWithSlug(ROUTES_ADMIN.PLAN.FORM.fullPath));
    }

    function editRegister(param: string) {
        onHandleNavigate(
            getLinkWithSlug(`${ROUTES_ADMIN.PLAN.FORM.fullPath}/${param}`)
        );
    }

    function removeRegisterRequest(id: number) {
        sweetAlert
            .fire({
                title: 'Você tem certeza?',
                text: 'Se você confirmar este registro será removido e está ação não poderá ser revertida!',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: theme.colors.primary,
                cancelButtonColor: theme.colors.danger,
                confirmButtonText: 'Sim, remover registro!',
                cancelButtonText: 'Não, cliquei errado!',
            })
            .then((result) => {
                if (result.isConfirmed) {
                    removeRegister(id);
                }
            });
    }

    return (
        <Layout
            titlePage="Painel Administrativo"
            breadcrumbs={[
                {
                    label: 'Home',
                    href: getLinkWithSlug(ROUTES_ACCOUNT.SELECTED.fullPath),
                },
                {
                    label: 'Planos',
                    href: getLinkWithSlug(ROUTES_ADMIN.PLAN.fullPath),
                },
            ]}
            title="Planos"
            buttonTitle={
                <Button color="nonary" onClick={() => newRegister()}>
                    Novo Registro
                </Button>
            }
        >
            <PlanListWrap className="plans-template">
                <Table
                    isLoading={isLoadingList}
                    columns={[
                        {
                            dataField: 'id',
                            text: 'ID',
                            sort: true,
                            headerStyle: { width: '10%' },
                        },
                        {
                            dataField: 'nome',
                            text: 'Nome',
                            sort: true,
                            headerStyle: { width: '40%' },
                        },
                        {
                            dataField: 'destaque',
                            text: 'Destaque',
                            sort: true,
                            formatter: (fieldValue) => (
                                <HelperBoolean value={Boolean(fieldValue)} />
                            ),
                            headerStyle: { width: '20%' },
                        },
                        {
                            dataField: 'valorMensal',
                            text: 'Valor Mensal',
                            sort: true,
                            formatter: (fieldValue) => (
                                <HelperMoney value={fieldValue} />
                            ),
                            headerStyle: { width: '20%' },
                        },
                        {
                            dataField: 'id',
                            text: 'Opções',
                            formatter: (fieldValue) =>
                                HelperOptions({
                                    edit: true,
                                    editFunction: () =>
                                        editRegister(fieldValue),
                                    removeFunction: () =>
                                        removeRegisterRequest(fieldValue),
                                }),
                            headerStyle: { width: '20%' },
                        },
                    ]}
                    data={data}
                />
            </PlanListWrap>
        </Layout>
    );
};

export default PlanList;
